import type { ClientOrganizationProfile } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useState } from 'react';

import { PersonIcon } from '@/assets';
import { useGetOwnOrganizations } from '@/features/api/hooks/grapevine-organization/useGetOwnOrganizations';
import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { OrganizationLogo } from '@/features/media/components/avatar/OrganizationLogo';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import {
  getPersonAvatarPicture,
  getPersonAvatarTextFallback
} from '@/features/media/utils/person';
import { SenderChip } from '@/features/sender-selection/components/SenderChip';
import { SenderSelectionBottomSheet } from '@/features/sender-selection/components/SenderSelectionBottomSheet';
import type { Sender } from '@/features/sender-selection/types/sender';
import { SenderType } from '@/features/sender-selection/types/sender';
import { getPublicName } from '@/shared/utils/string';

const AVATAR_SIZE = 24;

type Props = {
  /** Current sender — passed in by the owner (screen) so this component stays
   *  free of store coupling and can render inside a navigation header. */
  sender: Sender;
  /** Disables the chip (e.g. while submitting). */
  disabled?: boolean;
  /** Organizations offered in the picker — falls back to own organizations. */
  organizations?: ClientOrganizationProfile[];
  /** Show the personal profile entry in the picker. */
  showPersonalProfile?: boolean;
  onSenderSelect?: () => void;
};

export const SenderChipContainer: FC<Props> = ({
  sender,
  disabled,
  organizations,
  showPersonalProfile = true,
  onSenderSelect
}) => {
  const [sheetVisible, setSheetVisible] = useState(false);

  const { data: person } = useGetPerson();
  const { data: ownOrganizations } = useGetOwnOrganizations();

  const resolvedOrganizations = organizations ?? ownOrganizations;

  const senderUserLabel =
    getPublicName(person?.firstName, person?.lastName) || '…';

  const organization =
    sender.type === SenderType.ORGANIZATION
      ? resolvedOrganizations?.find((org) => org.id === sender.organizationId)
      : undefined;

  const openSheet = () => setSheetVisible(true);
  const closeSheet = () => setSheetVisible(false);

  const chip =
    sender.type === SenderType.ORGANIZATION ? (
      <SenderChip
        avatar={
          <OrganizationLogo
            mediaItem={organization?.logoImage}
            size={AVATAR_SIZE}
          />
        }
        label={organization?.name ?? '…'}
        disabled={disabled}
        onPress={openSheet}
      />
    ) : (
      <SenderChip
        avatar={
          <ProfileAvatar
            viewSize={AVATAR_SIZE}
            iconFallback={PersonIcon}
            mediaItem={person ? getPersonAvatarPicture(person) : undefined}
            textFallback={
              person ? getPersonAvatarTextFallback(person) : undefined
            }
            mediaSize={MediaItemSize.SMALL}
          />
        }
        label={senderUserLabel}
        disabled={disabled}
        onPress={openSheet}
      />
    );

  return (
    <>
      {chip}
      {!disabled && (
        <SenderSelectionBottomSheet
          isVisible={sheetVisible}
          onClose={closeSheet}
          organizations={resolvedOrganizations}
          showPersonalProfile={showPersonalProfile}
          onSenderSelect={onSenderSelect}
        />
      )}
    </>
  );
};
